import { useEffect, useRef, useState } from 'react';
import { LocalLink as Link } from '@/lib/locale';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatPrice, isSoldOut, productTheme, splitProductName } from '@/lib/catalog';
import { track } from '@/lib/analytics';
import { ProductImage } from '@/lib/images';

// Shop grid: 2 / 3 / 4 columns
const SHOP_SIZES = '(min-width: 1280px) 300px, (min-width: 768px) 33vw, 50vw';

export const ProductCard = ({ product, onAddToCart, priority = false, listName = 'catalog', className = '', sizes = SHOP_SIZES }) => {
  const { t } = useTranslation();
  const [added, setAdded] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const { brand, type } = splitProductName(product.name);
  const { bg } = productTheme(product);
  const soldOut = isSoldOut(product);
  const href = `/product/${product.product_id}`;

  const handleSelect = () => track('select_item', { item_list_id: listName, item_id: product.product_id });

  const handleAdd = () => {
    if (soldOut) return;
    onAddToCart(product);
    track('add_to_cart', { item_id: product.product_id, value: product.price, currency: 'EUR' });
    setAdded(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setAdded(false), 1500);
  };

  return (
    <article
      data-testid={`product-card-${product.product_id}`}
      className={`group relative flex flex-col gap-3 rounded-card p-3.5 ${bg} transition duration-200 ease-out motion-safe:hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgb(var(--ink)/0.35)] ${className}`}
    >
      <Link
        to={href}
        onClick={handleSelect}
        className="relative block aspect-square overflow-hidden rounded-photo bg-candy-surface"
        tabIndex={-1}
        aria-hidden="true"
      >
        <ProductImage
          src={product.image_url}
          alt={product.name}
          sizes={sizes}
          width="400"
          height="400"
          priority={priority}
          className="h-full w-full object-contain p-3 transition-transform duration-200 ease-out motion-safe:group-hover:scale-[1.03]"
        />
        <span className="absolute right-2.5 top-2.5 rounded-full bg-candy-ink px-2.5 py-1 text-xs font-bold text-white">
          {t('product.limited')}
        </span>
      </Link>

      <div className="flex-1 px-1.5">
        {brand && <span className="text-[13px] font-bold text-candy-ink-muted">{brand}</span>}
        {/* Long supplier titles are clamped to 3 lines so one product can't stretch its row */}
        <h3
          className="mt-0.5 line-clamp-3 font-heading text-lg font-bold leading-[1.15] tracking-[-0.01em] text-candy-ink md:text-xl"
          title={type}
        >
          <Link
            to={href}
            onClick={handleSelect}
            className="rounded-sm hover:underline decoration-2 underline-offset-2"
            data-testid={`product-name-${product.product_id}`}
          >
            {type}
          </Link>
        </h3>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-1.5 gap-y-2 pb-0.5 pl-1.5 pr-0.5">
        <span
          className="font-heading text-2xl font-extrabold text-candy-ink"
          data-testid={`product-price-${product.product_id}`}
        >
          {formatPrice(product.price)}
        </span>
        <button
          type="button"
          data-testid={`add-to-cart-btn-${product.product_id}`}
          onClick={handleAdd}
          disabled={soldOut}
          aria-label={soldOut ? `${product.name} — ${t('product.soldOut')}` : `${t('product.addToCart')}: ${product.name}`}
          className={`inline-flex h-[46px] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-4 text-sm font-bold transition-colors duration-150 ease-out sm:flex-none ${
            soldOut
              ? 'cursor-not-allowed bg-candy-ink/20 text-candy-ink/60'
              : 'bg-candy-ink text-white hover:bg-candy-ink/90'
          }`}
        >
          {soldOut ? (
            t('product.soldOut')
          ) : added ? (
            <>
              {t('product.added')} <Check className="h-4 w-4" aria-hidden="true" />
            </>
          ) : (
            t('product.addToCart')
          )}
        </button>
      </div>
      <span className="sr-only" aria-live="polite">{added ? t('product.addedLive', { name: product.name }) : ''}</span>
    </article>
  );
};
